/**
 * MetaTags — calm document.head manager.
 *
 * Sets the page's <title>, meta description, and Open Graph + Twitter card
 * tags based on the current route. Mount once per page. Effects are undone
 * (title restored) on unmount so server-rendered defaults stay clean for
 * routes that don't specify their own.
 *
 * Per the product copy spec §9 — every marketing route gets its own SEO
 * metadata, never the default.
 */
import { useEffect } from "react";

import { ACTIVATED_LOCALES, LOCALES, SOURCE_LOCALE } from "../lib/locales-config";

const DEFAULTS = {
  title: "Alouvya — Childcare communication, reimagined",
  description:
    "Next-generation childcare communication infrastructure. Real-time child status, one-tap staff workflows, privacy-first by design.",
  ogImage: "/brand/og-1200x630.png",
};

function setMeta(name, content, attribute = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Manage hreflang alternates per spec §14.2 — one <link> per activated locale
 *  plus an `x-default`. Removes prior managed links on unmount so a route
 *  without MetaTags doesn't carry stale hreflangs. */
function syncHreflang() {
  const existing = document.head.querySelectorAll('link[rel="alternate"][data-alouvya-hreflang]');
  existing.forEach((el) => el.remove());
  const path = window.location.pathname + window.location.search + window.location.hash;
  const origin = window.location.origin;
  const activated = [...ACTIVATED_LOCALES];
  for (const code of activated) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", code);
    el.setAttribute("href", `${origin}${path}`); // single-app SPA: same path, locale resolved client-side
    el.setAttribute("data-alouvya-hreflang", "1");
    document.head.appendChild(el);
  }
  // x-default → source locale
  const xd = document.createElement("link");
  xd.setAttribute("rel", "alternate");
  xd.setAttribute("hreflang", "x-default");
  xd.setAttribute("href", `${origin}${path}`);
  xd.setAttribute("data-alouvya-hreflang", "1");
  document.head.appendChild(xd);
}

export default function MetaTags({
  title,
  description,
  ogImage,
  ogType = "website",
  canonical,
}) {
  useEffect(() => {
    const finalTitle = title || DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const rawOg = ogImage || DEFAULTS.ogImage;
    const finalOg =
      rawOg.startsWith("http://") || rawOg.startsWith("https://")
        ? rawOg
        : `${window.location.origin}${rawOg}`;

    const prevTitle = document.title;
    document.title = finalTitle;

    setMeta("description", finalDesc);
    setMeta("og:title", finalTitle, "property");
    setMeta("og:description", finalDesc, "property");
    setMeta("og:image", finalOg, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:locale", document.documentElement.lang || SOURCE_LOCALE.split("-")[0], "property");
    if (canonical) setMeta("og:url", canonical, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDesc);
    setMeta("twitter:image", finalOg);

    syncHreflang();

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, ogImage, ogType, canonical]);

  return null;
}

// Re-export for tests / lint usage
export { ACTIVATED_LOCALES, LOCALES };
